/* eslint-disable no-console */
import { handleActions } from 'redux-actions';
import { RemoteData } from 'rf-lib';
import type { GameRunnerState, WheelEvent } from '@lykkehjulet/db-model';
import * as t from './constants';

type AnyObj = Record<string, any>;
type AnyAction = { type: string; payload?: any };
type TicketsByPlayer = Record<string, number>;
type RemainingPlayer = {
  playerId: string;
  tickets: number;
};

type AnimationState = {
  lastShownAnimation: number;
  animationGameState: string;
  isSpinning: boolean;
  isBehind: boolean;
  remainingPlayers: RemainingPlayer[];
};

const defaultGameRunnerState: GameRunnerState = {
  gameState: 'Not started',
  events: [],
  winnerList: []
};

const defaultAnimationState: AnimationState = {
  lastShownAnimation: -1,
  animationGameState: 'Not Started',
  isSpinning: false,
  isBehind: false,
  remainingPlayers: [],
};

const defaultState: AnyObj = {
  currentGameState: RemoteData.NotAsked,
  fetchedGame: RemoteData.NotAsked,
  gamesState: RemoteData.NotAsked,
  gameRunnerState: defaultGameRunnerState,
  animationState: defaultAnimationState,
  ticketsState: {},
  transactionsState: {},
  errors: [],
  fillTestDataRemoteData: RemoteData.NotAsked,
  groupDetailsRemoteData: RemoteData.NotAsked,
  updateGameRemoteData: RemoteData.NotAsked,
  resetGameRemoteData: RemoteData.NotAsked,
};

function getAnimationGameState(gameState: string, lastShownAnimation: number, events: WheelEvent[]) {
  if (gameState !== 'Ended') return gameState;

  if (lastShownAnimation === events.length) {
    return gameState;
  }
  return 'Started';
}

function getIsBehind(lastShownAnimation: number, events: WheelEvent[]) {
  return events.length > lastShownAnimation +2;
}

const getRemainingPlayers = (
  tickets: TicketsByPlayer | undefined,
  lastShownAnimation: number,
  events: WheelEvent[]
) => {
  let gamePlayers: RemainingPlayer[] = [];

  const adjustedEvents = events ? events.slice(0, lastShownAnimation) : [];

  const applyTicketDelta = (
    acc: Record<string, number>,
    playerId: string,
    delta: number
  ) => {
    acc[playerId] = (acc[playerId] || 0) + delta;
  };

  const groupedTicketDelta = adjustedEvents.reduce((acc: Record<string, number>, event: WheelEvent) => {
    switch (event.type) {
      case 'out':
        if (event.playerId) {
          applyTicketDelta(acc, event.playerId, 1);
        }
        break;
      case 'in':
        if (event.playerId) {
          applyTicketDelta(acc, event.playerId, -1);
        }
        break;
      case 'gifts':
        event.playerIds.forEach((playerId: string) => {
          applyTicketDelta(acc, playerId, -1);
        });
        break;
      default:
        break;
    }

    return acc;
  }, {});

  const gameTickets = tickets || {};
  const playerIds = Array.from(new Set([
    ...Object.keys(gameTickets),
    ...Object.keys(groupedTicketDelta)
  ]));

  gamePlayers = playerIds
    .reduce((result: RemainingPlayer[], playerId: string) => {
      const ticketDelta = groupedTicketDelta[playerId] || 0;
      const baseTickets = gameTickets[playerId] || 0;
      const currentTickets = baseTickets - ticketDelta;
      if (currentTickets > 0) {
        result.push({
          playerId,
          tickets: currentTickets
        });
      }
      return result;
    }, []);

  return gamePlayers;
};

const mapEvents = (original: unknown): WheelEvent[] => {
  if (!original) return [];
  if (Array.isArray(original)) {
    return original.filter((event): event is WheelEvent => Boolean(event));
  }
  if (typeof original === 'object') {
    return Object.values(original).filter((event): event is WheelEvent => Boolean(event));
  }
  return [];
};

const updateGameRunnerState = (state: AnyObj, payload: any) => {
  const typedPayload = (payload || {}) as Partial<GameRunnerState>;
  const events = mapEvents(typedPayload.events);
  let { lastShownAnimation } = state.animationState;
  const gameState = typedPayload.gameState || 'Not started';
  const currentGameState = state.currentGameState as { data?: { tickets?: TicketsByPlayer } };
  const { tickets } = currentGameState.data || {};

  // Make sure lastShownAnimation is not past end of already known events
  if (lastShownAnimation > events.length) {
    lastShownAnimation = events.length;
  }
  if (!state.animationState.isSpinning && lastShownAnimation === -1) {
    if (gameState === 'Ended') {
      lastShownAnimation = events.length;
    } else if (gameState === 'Started' && events.length > 0) {
      // If animation has not started, assume that we are refreshing mid-game.
      lastShownAnimation = events.length - 1;
    }
  }



  const remainingPlayers =
    getRemainingPlayers(tickets, lastShownAnimation, events);

  const animationGameState = getAnimationGameState(gameState, lastShownAnimation, events);
  const isBehind = getIsBehind(lastShownAnimation, events);

  return {
    gameRunnerState: {
      ...state.gameRunnerState,
      ...typedPayload,
      events,
    },
    animationState: {
      ...state.animationState,
      lastShownAnimation,
      animationGameState,
      isBehind,
      remainingPlayers
    }
  };
};

const updateCurrentGameState = (state: AnyObj, payload: any) => {
  const typedPayload = (payload || {}) as { tickets?: TicketsByPlayer };
  const tickets = typedPayload.tickets || {};

  const playersWithTickets = (Object.keys(tickets))
    .filter(x => {
      if (tickets[x] === 0) {
        return false;
      }
      return true;
    })
    .map(x => (
      {
        playerId: x,
        tickets: tickets[x]
      }
    ));
  return {
    ...state.currentGameState,
    ...typedPayload,
    playersWithTickets,
  };
};

const updateLastShownAnimation = (state: AnyObj, lastShownAnimation: number) => {
  const currentGameState = state.currentGameState as { data?: { tickets?: TicketsByPlayer } };
  const { tickets } = currentGameState.data || {};
  const runnerState = state.gameRunnerState as GameRunnerState;
  const events = mapEvents(runnerState.events || []);
  const remainingPlayers =
    getRemainingPlayers(tickets, lastShownAnimation, events);
  const animationGameState = getAnimationGameState(runnerState.gameState || 'Not started', lastShownAnimation, events);
  const isBehind = getIsBehind(lastShownAnimation, events);

  return {
    gameRunnerState: {
      ...state.gameRunnerState,
    },
    animationState: {
      ...state.animationState,
      lastShownAnimation,
      isSpinning: false,
      animationGameState,
      isBehind,
      remainingPlayers,
    }
  };
};

const reducer = handleActions({
  [t.FETCH_GAME]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    fetchedGame: RemoteData.Loading
  }),

  [t.FETCH_GAME_FAILURE]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    fetchedGame: RemoteData.Failure(action.payload)
  }),

  [t.FETCH_GAME_SUCCESS]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    fetchedGame: RemoteData.Success(action.payload)
  }),

  [t.RESET_CURRENT_GAME]: (state: AnyObj, action: AnyAction) => defaultState,

  [t.REANIMATE]: (state: AnyObj, action: AnyAction)  => {
    const updated = updateLastShownAnimation(state, 0);
    return {
      ...state,
      ...updated,
      gameRunnerState: {
        ...updated.gameRunnerState,
        gameState: 'Started',
      }
    };
  },



  [t.UPDATE_GAME]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    updateGameRemoteData: RemoteData.Loading
  }),

  [t.UPDATE_GAME_FAILURE]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    updateGameRemoteData: RemoteData.Failure(action.payload)
  }),

  [t.UPDATE_GAME_SUCCESS]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    updateGameRemoteData: RemoteData.Success(action.payload)
  }),

  [t.RESET_UPDATE_GAME_STATE]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    updateGameRemoteData: RemoteData.NotAsked
  }),

  [t.FETCH_GAMES]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    gamesState: RemoteData.Loading
  }),

  [t.FETCH_GAMES_FAILURE]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    gamesState: RemoteData.Failure(action.payload)
  }),

  [t.FETCH_GAMES_SUCCESS]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    gamesState: RemoteData.Success(action.payload)
  }),

  [t.START_GAME]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    gameRunnerState: {
      ...state.gameRunnerState,
      gameState: 'Starter spill...'
    }
  }),

  [t.START_GAME_FAILURE]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    errors: action.payload
  }),

  [t.START_GAME_SUCCESS]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    gameRunnerState: {
      ...state.gameRunnerState,
      gameState: 'Started'
    }
  }),

  [t.GAME_LISTENER_FAILURE]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    currentGameState: RemoteData.Failure(action.payload)
  }),

  [t.GAME_LISTENER_SUCCESS]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    currentGameState: RemoteData.Success(updateCurrentGameState(state, action.payload))
  }),

  [t.GAMERUNNER_LISTENER_FAILURE]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    errors: action.payload
  }),

  [t.GAMERUNNER_LISTENER_SUCCESS]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    ...updateGameRunnerState(state, action.payload)
  }),

  [t.REMOVE_GAMERUNNER_LISTENER]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    gameRunnerState: defaultGameRunnerState,
    animationState: defaultAnimationState,
  }),

  [t.TICKETS_LISTENER_FAILURE]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    errors: action.payload
  }),

  [t.TICKETS_LISTENER_SUCCESS]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    ticketsState: { ...state.ticketsState, ...action.payload }
  }),

  [t.REMOVE_TICKETS_LISTENER]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    ticketsState: {},
  }),

  [t.ADD_TRANSACTION_LISTENER]: (state: AnyObj, action: AnyAction) => ({
    ...state, transactionsState: RemoteData.Loading
  }),
  [t.TRANSACTION_LISTENER_SUCCESS]: (state: AnyObj, action: AnyAction) => ({
    ...state, transactionsState: RemoteData.Success(action.payload)
  }),

  [t.TRANSACTION_LISTENER_FAILURE]: (state: AnyObj, action: AnyAction) => ({
    ...state, transactionsState: RemoteData.Failure(action.payload)
  }),

  [t.REMOVE_TRANSACTION_LISTENER]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    balanceState: {},
  }),

  [t.FILL_TEST_DATA]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    fillTestDataRemoteData: RemoteData.Loading
  }),

  [t.FILL_TEST_DATA_FAILURE]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    fillTestDataRemoteData: RemoteData.Failure(action.payload)
  }),

  [t.FILL_TEST_DATA_SUCCESS]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    fillTestDataRemoteData: RemoteData.Success(action.payload)
  }),

  [t.RESET_FILL_TEST_DATA_REMOTEDATA]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    fillTestDataRemoteData: RemoteData.NotAsked
  }),

  [t.RESET_GAME]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    resetGameRemoteData: RemoteData.Loading
  }),

  [t.RESET_GAME_FAILURE]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    resetGameRemoteData: RemoteData.Failure(action.payload)
  }),

  [t.RESET_GAME_SUCCESS]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    resetGameRemoteData: RemoteData.Success(action.payload)
  }),

  [t.RESET_RESET_GAME_REMOTEDATA]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    resetGameRemoteData: RemoteData.NotAsked
  }),

  [t.FETCH_GROUP_DETAILS]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    groupDetailsRemoteData: RemoteData.Loading
  }),

  [t.FETCH_GROUP_DETAILS_FAILURE]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    groupDetailsRemoteData: RemoteData.Failure(action.payload)
  }),

  [t.FETCH_GROUP_DETAILS_SUCCESS]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    groupDetailsRemoteData: RemoteData.Success(action.payload)
  }),

  [t.SET_CURRENT_ANIMATION]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    ...updateLastShownAnimation(state, action.payload)
  }),
  [t.SET_IS_SPINNING]: (state: AnyObj, action: AnyAction) => ({
    ...state,
    animationState: {
      ...state.animationState,
      isSpinning: true,
    }
  }),
}, defaultState);

export default reducer;
